import type { Destination } from './labels'
import type { MakeVersionsBody } from './api'

export type VersionFormat = 'short' | 'long' | 'deep'
export type VersionChoice = { format: VersionFormat; on: boolean; destination: Destination }
export type VersionForm = { choices: VersionChoice[]; start: 'ai' | 'blank'; note: string }

export const VERSION_FORMATS: { format: VersionFormat; label: string; words: string }[] = [
  { format: 'short', label: 'Short', words: 'about 250 words' },
  { format: 'long', label: 'Long', words: 'about 1,000 words' },
  { format: 'deep', label: 'Deep research', words: '2,000 words or more' },
]
const RANK: Record<VersionFormat, number> = { short: 0, long: 1, deep: 2 }

export function isLonger(target: VersionFormat, source: VersionFormat | null): boolean {
  return RANK[target] > RANK[source ?? 'long']
}

/** Where a version goes by default: a shorter version of a members-only piece is for the free
 *  section (the main use case); otherwise the source's own audience. Never members-only on a
 *  title without membership publishing. */
export function defaultVersionDestination(target: VersionFormat, sourceFormat: VersionFormat | null,
                                          sourceDestination: Destination, membershipOk = true): Destination {
  if (!membershipOk) return 'section'
  if (sourceDestination === 'membership' && RANK[target] < RANK[sourceFormat ?? 'long']) return 'section'
  return sourceDestination
}

/** The source's audience: a draft's own destination, else the archive site it lives on. */
export function destinationForKind(kind: 'draft' | 'member' | 'public', draftDestination?: Destination | null): Destination {
  if (draftDestination) return draftDestination
  return kind === 'member' ? 'membership' : 'section'
}

export function initialVersionForm(sourceFormat: VersionFormat | null, sourceDestination: Destination,
                                   membershipOk = true): VersionForm {
  const firstOther = VERSION_FORMATS.find((f) => f.format !== sourceFormat)?.format
  return {
    choices: VERSION_FORMATS.map((f) => ({
      format: f.format, on: f.format === firstOther,
      destination: defaultVersionDestination(f.format, sourceFormat, sourceDestination, membershipOk),
    })),
    start: 'ai',
    note: '',
  }
}

export function versionBlock(f: VersionForm): string | null {
  return f.choices.some((c) => c.on) ? null : 'Pick at least one format.'
}

export function versionsBody(f: VersionForm, source: { draft_id?: number; article_id?: number }): MakeVersionsBody {
  return {
    source, start: f.start, note: f.note.trim(),
    versions: f.choices.filter((c) => c.on).map((c) => ({ format: c.format, destination: c.destination })),
  }
}
